import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
const formatDateForBackend = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('-');
  return `${day}-${month}-${year}`;
};

// Helper function to format date from DD-MM-YYYY or array format to YYYY-MM-DD for input
const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  
  // Handle array format [year, month, day]
  if (Array.isArray(dateValue) && dateValue.length >= 3) {
    const [year, month, day] = dateValue;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  
  // Handle DD-MM-YYYY format
  if (typeof dateValue === 'string' && dateValue.includes('-')) {
    const parts = dateValue.split('-');
    if (parts[0].length === 2) {
      // DD-MM-YYYY format
      const [day, month, year] = parts;
      return `${year}-${month}-${day}`;
    } else {
      // Already YYYY-MM-DD format
      return dateValue.split('T')[0];
    }
  }
  
  // Handle timestamp
  try {
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (e) {
    // ignore
  }
  
  return '';
};

const ConferenceManagement = () => {
  const [activeConferences, setActiveConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [selectedConference, setSelectedConference] = useState(null);
  const [conferenceDetails, setConferenceDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [alert, setAlert] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    bannerUrl: '',
    startDate: '',
    endDate: '',
    place: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    prefix: '',
    paperPrefix: '',
    capacity: '',
    emailFrom: '',
    emailCc: '',
    active: true
  });

  const [errors, setErrors] = useState({});

  // Fetch active conferences for dropdown
  const fetchActiveConferences = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/active`);
      if (response.ok) {
        const data = await response.json();
        setActiveConferences(data);
      } else {
        showAlert('Failed to fetch active conferences', 'error');
      }
    } catch (error) {
      console.error('Error fetching active conferences:', error);
      showAlert('Error fetching active conferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch conference details by ID
  const fetchConferenceDetails = async (id) => {
    if (!id) return;
    
    setDetailsLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/${id}`);
      if (response.ok) {
        const data = await response.json();
        setConferenceDetails(data);
      } else {
        showAlert('Failed to fetch conference details', 'error');
        setConferenceDetails(null);
      }
    } catch (error) {
      console.error('Error fetching conference details:', error);
      showAlert('Error fetching conference details', 'error');
      setConferenceDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Load active conferences on mount
  useEffect(() => {
    fetchActiveConferences();
  }, []);

  // Load details when selected conference changes
  useEffect(() => {
    if (selectedConference?.id) {
      fetchConferenceDetails(selectedConference.id);
    } else {
      setConferenceDetails(null);
    }
  }, [selectedConference]);

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  // Helper function to format dates safely
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    
    try {
      // Handle array format [year, month, day]
      if (Array.isArray(dateValue) && dateValue.length >= 3) {
        const [year, month, day] = dateValue;
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString();
      }
      
      // Handle string or timestamp format
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
      
      // If all else fails, return the original value
      return String(dateValue);
    } catch (e) {
      return String(dateValue);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: fieldValue }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateEmailList = (emailList) => {
    if (!emailList || !emailList.trim()) return true;
    const emails = emailList.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emails.every(email => emailRegex.test(email));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.domain.trim()) newErrors.domain = 'Domain is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.place.trim()) newErrors.place = 'Place is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.prefix.trim()) newErrors.prefix = 'Prefix is required';
    if (formData.paperPrefix && formData.paperPrefix.length > 20) {
      newErrors.paperPrefix = 'Paper prefix must be 20 characters or less';
    }
    
    if (formData.capacity && (isNaN(formData.capacity) || parseInt(formData.capacity) < 0)) {
      newErrors.capacity = 'Capacity must be a positive number';
    }

    // Validate CC emails (comma-separated)
    if (formData.emailCc && !validateEmailList(formData.emailCc)) {
      newErrors.emailCc = 'Invalid email format. Use comma-separated valid emails';
    }

    // Validate date range
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    setModalMode('create');
    setFormData({
      name: '',
      domain: '',
      bannerUrl: '',
      startDate: '',
      endDate: '',
      place: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      prefix: '',
      paperPrefix: '',
      capacity: '',
      emailFrom: '',
      emailCc: '',
      active: true
    });
    setErrors({});
    setShowModal(true);
  };

  const handleEdit = (conference) => {
    setModalMode('edit');
    setFormData({
      name: conference.name || '',
      domain: conference.domain || '',
      bannerUrl: conference.bannerUrl || '',
      startDate: formatDateForInput(conference.startDate),
      endDate: formatDateForInput(conference.endDate),
      place: conference.place || '',
      address: conference.address || '',
      city: conference.city || '',
      state: conference.state || '',
      country: conference.country || '',
      zipCode: conference.zipCode || '',
      prefix: conference.prefix || '',
      paperPrefix: conference.paperPrefix || '',
      capacity: conference.capacity || '',
      emailFrom: conference.emailFrom || '',
      emailCc: conference.emailCc || '',
      // Handle both 'active' and 'isActive' from backend
      active: conference.active !== undefined ? conference.active : 
              (conference.isActive !== undefined ? conference.isActive : true)
    });
    setErrors({});
    setShowModal(true);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      ...formData,
      startDate: formatDateForBackend(formData.startDate),
      endDate: formatDateForBackend(formData.endDate),
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
      isActive: formData.active,  // Map 'active' to 'isActive' for backend
      active: formData.active     // Also send as 'active' for compatibility
    };

    // Add ID for update operations
    if (modalMode === 'edit' && conferenceDetails?.id) {
      payload.id = conferenceDetails.id;
    }

    try {
      const url = modalMode === 'create' 
        ? CONFERENCE_API
        : `${CONFERENCE_API}/${conferenceDetails.id}`;
      
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      console.log('🔄 Submitting conference:', { mode: modalMode, url, method, payload });

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      console.log('📡 Response status:', response.status, response.statusText);

      if (response.ok) {
        showAlert(
          `Conference ${modalMode === 'create' ? 'created' : 'updated'} successfully`,
          'success'
        );
        setShowModal(false);
        fetchActiveConferences();
        
        // If conference was set to inactive, clear the selection and details
        if (modalMode === 'edit' && !formData.active) {
          setSelectedConferenceId('');
          setSelectedConference(null);
          setConferenceDetails(null);
        } else if (modalMode === 'edit' && selectedConferenceId) {
          // Refresh details if editing current conference and still active
          fetchConferenceDetails(selectedConferenceId);
        }
      } else {
        // Try to parse error response
        let errorMessage = `Failed to ${modalMode} conference`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || JSON.stringify(errorData);
        } catch (e) {
          // If JSON parsing fails, try text
          const errorText = await response.text();
          if (errorText) {
            errorMessage = errorText;
          }
        }
        showAlert(`${errorMessage}`, 'error');
        // Don't close modal on error so user can see the message and fix issues
      }
    } catch (error) {
      console.error(`Error ${modalMode}ing conference:`, error);
      showAlert(`Error ${modalMode}ing conference: ${error.message}`, 'error');
      // Don't close modal on error so user can see the message and fix issues
    }
  };

  const filteredConferences = activeConferences;

  return (
    <div>
      <div className="content-header">
        <h1>Conference Management</h1>
        <div className="breadcrumb">Admin / Conference</div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`}>
          <span>{alert.message}</span>
        </div>
      )}

      <div className="content-body">
        <div className="action-bar">
          <div className="conference-selector">
            <label htmlFor="conference-select" style={{ marginRight: '10px', fontWeight: 'bold' }}>
              Select Active Conference:
            </label>
            <select
              id="conference-select"
              value={selectedConferenceId}
              onChange={(e) => {
                const conferenceId = e.target.value;
                setSelectedConferenceId(conferenceId);
                const conf = activeConferences.find(c => c.id.toString() === conferenceId);
                setSelectedConference(conf || null);
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                fontSize: '14px',
                minWidth: '300px'
              }}
              disabled={loading}
            >
              <option value="">All Conferences</option>
              {activeConferences.map((conf) => (
                <option key={conf.id} value={conf.id}>
                  {conf.name}
                </option>
              ))}
            </select>
          </div>
          <div className="action-buttons">
            <button className="btn btn-primary" onClick={handleCreate}>
              ➕ Create Conference
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading active conferences...</p>
          </div>
        ) : activeConferences.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h3>No Active Conferences Found</h3>
            <p>Get started by creating your first conference</p>
            <button className="btn btn-primary" onClick={handleCreate}>
              Create Conference
            </button>
          </div>
        ) : detailsLoading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading conference details...</p>
          </div>
        ) : conferenceDetails ? (
          <div className="conference-details-card">
            <div className="details-header">
              <h2>{conferenceDetails.name}</h2>
              <div className="details-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => handleEdit(conferenceDetails)}
                >
                  ✏️ Edit
                </button>
              </div>
            </div>

            <div className="details-grid">
              <div className="detail-item">
                <label>Conference Name:</label>
                <span>{conferenceDetails.name}</span>
              </div>
              <div className="detail-item">
                <label>Domain:</label>
                <span>{conferenceDetails.domain}</span>
              </div>
              <div className="detail-item">
                <label>Email From:</label>
                <span>{conferenceDetails.emailFrom || 'Not set'}</span>
              </div>
              <div className="detail-item full-width">
                <label>Email CC:</label>
                <span>{conferenceDetails.emailCc || 'Not set'}</span>
              </div>
              <div className="detail-item">
                <label>Prefix:</label>
                <span>{conferenceDetails.prefix}</span>
              </div>
              <div className="detail-item">
                <label>Paper Prefix:</label>
                <span>{conferenceDetails.paperPrefix || 'Not set'}</span>
              </div>
              <div className="detail-item">
                <label>Capacity:</label>
                <span>{conferenceDetails.capacity || 'Not set'}</span>
              </div>
              <div className="detail-item">
                <label>Start Date:</label>
                <span>{formatDate(conferenceDetails.startDate)}</span>
              </div>
              <div className="detail-item">
                <label>End Date:</label>
                <span>{formatDate(conferenceDetails.endDate)}</span>
              </div>
              <div className="detail-item">
                <label>Venue:</label>
                <span>{conferenceDetails.place}</span>
              </div>
              <div className="detail-item">
                <label>City:</label>
                <span>{conferenceDetails.city}</span>
              </div>
              <div className="detail-item">
                <label>State:</label>
                <span>{conferenceDetails.state}</span>
              </div>
              <div className="detail-item">
                <label>Country:</label>
                <span>{conferenceDetails.country}</span>
              </div>
              <div className="detail-item">
                <label>Zip Code:</label>
                <span>{conferenceDetails.zipCode || 'Not set'}</span>
              </div>
              {conferenceDetails.address && (
                <div className="detail-item full-width">
                  <label>Address:</label>
                  <span>{conferenceDetails.address}</span>
                </div>
              )}
              {conferenceDetails.bannerUrl && (
                <div className="detail-item full-width">
                  <label>Banner URL:</label>
                  <span style={{ wordBreak: 'break-all' }}>{conferenceDetails.bannerUrl}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">👆</div>
            <h3>Select a Conference</h3>
            <p>Choose a conference from the dropdown above to view its details</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Create Conference' : 'Edit Conference'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} id="conference-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>
                      Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter conference name"
                    />
                    {errors.name && <span className="form-error">{errors.name}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Domain <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="domain"
                      value={formData.domain}
                      onChange={handleInputChange}
                      placeholder="e.g., example.com"
                    />
                    {errors.domain && <span className="form-error">{errors.domain}</span>}
                  </div>

                  <div className="form-group">
                    <label>Email From</label>
                    <input
                      type="email"
                      name="emailFrom"
                      value={formData.emailFrom}
                      onChange={handleInputChange}
                      placeholder="noreply@conference.com"
                    />
                    {errors.emailFrom && <span className="form-error">{errors.emailFrom}</span>}
                  </div>

                  <div className="form-group full-width">
                    <label>Email CC (comma-separated)</label>
                    <input
                      type="text"
                      name="emailCc"
                      value={formData.emailCc}
                      onChange={handleInputChange}
                      placeholder="admin@example.com, support@example.com"
                    />
                    {errors.emailCc && <span className="form-error">{errors.emailCc}</span>}
                    <small style={{ color: '#666', fontSize: '12px' }}>Enter multiple emails separated by commas</small>
                  </div>

                  <div className="form-group full-width">
                    <label>Banner URL</label>
                    <input
                      type="url"
                      name="bannerUrl"
                      value={formData.bannerUrl}
                      onChange={handleInputChange}
                      placeholder="https://example.com/banner.jpg"
                    />
                    {errors.bannerUrl && <span className="form-error">{errors.bannerUrl}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Start Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                    />
                    {errors.startDate && <span className="form-error">{errors.startDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      End Date <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                    />
                    {errors.endDate && <span className="form-error">{errors.endDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Place <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="place"
                      value={formData.place}
                      onChange={handleInputChange}
                      placeholder="Venue name"
                    />
                    {errors.place && <span className="form-error">{errors.place}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="City"
                    />
                    {errors.city && <span className="form-error">{errors.city}</span>}
                  </div>

                  <div className="form-group full-width">
                    <label>Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Full address"
                    />
                    {errors.address && <span className="form-error">{errors.address}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      State <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                    {errors.state && <span className="form-error">{errors.state}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Country <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      placeholder="Country"
                    />
                    {errors.country && <span className="form-error">{errors.country}</span>}
                  </div>

                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="Postal code"
                    />
                    {errors.zipCode && <span className="form-error">{errors.zipCode}</span>}
                  </div>

                  <div className="form-group">
                    <label>
                      Prefix <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="prefix"
                      value={formData.prefix}
                      onChange={handleInputChange}
                      placeholder="e.g., CONF2025"
                      maxLength={5}
                    />
                    {errors.prefix && <span className="form-error">{errors.prefix}</span>}
                  </div>

                  <div className="form-group">
                    <label>Paper Prefix</label>
                    <input
                      type="text"
                      name="paperPrefix"
                      value={formData.paperPrefix}
                      onChange={handleInputChange}
                      placeholder="e.g., ASCO"
                      maxLength={20}
                    />
                    {errors.paperPrefix && <span className="form-error">{errors.paperPrefix}</span>}
                  </div>

                  <div className="form-group">
                    <label>Capacity</label>
                    <input
                      type="number"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleInputChange}
                      placeholder="Maximum attendees"
                      min="0"
                    />
                    {errors.capacity && <span className="form-error">{errors.capacity}</span>}
                  </div>

                  {modalMode === 'edit' && (
                    <div className="form-group">
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="active"
                          checked={formData.active}
                          onChange={handleInputChange}
                          style={{ marginRight: '8px', cursor: 'pointer' }}
                        />
                        Active
                      </label>
                    </div>
                  )}
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button type="submit" form="conference-form" className="btn btn-primary">
                {modalMode === 'create' ? 'Create' : 'Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenceManagement;
